import { memo, useCallback, useMemo } from "react"
import { ChatCard } from "@/components/ui/chat-card"
import RouteStopDetailsPopover, {
  CarSpeedPopover,
} from "@/components/RouteStopDetailPopover"
import useForeignChatCardData from "@/components/chatList/useForeignChatCardData"

type ForeignChatRowProps = {
  jid: string
  name: string
  onClick: () => void
}

/** A chat that belongs to another dispatcher. It carries the same card as a
 * chat of our own — the route, GPS and temperature badges all come from
 * account-wide state, and the preview from the room's archive. Only the unread
 * count is missing, because it lives in the owner's client rather than on the
 * server. */
const ForeignChatRow = ({ jid, name, onClick }: ForeignChatRowProps) => {
  const cardProps = useForeignChatCardData(jid, name)
  const handleClick = useCallback(() => onClick(), [onClick])

  const popoverContent = useMemo(
    () =>
      cardProps.nextStop ? (
        <RouteStopDetailsPopover
          routeStop={cardProps.nextStop}
          speedLabel={cardProps.speedLabel}
        />
      ) : cardProps.speedLabel ? (
        <CarSpeedPopover speedLabel={cardProps.speedLabel} />
      ) : undefined,
    [cardProps.nextStop, cardProps.speedLabel],
  )

  return (
    <ChatCard
      dataTestId={`foreign-chat-${jid}`}
      name={name}
      onClick={handleClick}
      popoverContent={popoverContent}
      {...cardProps}
    />
  )
}

export default memo(ForeignChatRow)
